const TAU = Math.PI * 2;
const DPR_LIMIT = 1.75;
const NODE_COUNT = 26;
const PACKET_COUNT = 6;

// The pads are real network elements — this is an SS7 map, not decoration.
const NODE_LABELS = ["STP-1", "MSC", "SMSC", "HLR", "GMSC", "STP-2", "IGW"];
const ACCENT = "230, 199, 110";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const createRandom = (seed) => () => {
  seed = (seed * 1664525 + 1013904223) % 4294967296;
  return seed / 4294967296;
};

// Deterministic node lattice with nearest-neighbour links, so the board
// looks identical on every visit instead of shifting like a starfield.
const createCircuit = () => {
  const random = createRandom(0x0f0a2026);
  let labelIndex = 0;

  const nodes = Array.from({ length: NODE_COUNT }, () => {
    const x = (random() - 0.5) * 2.2;
    const y = (random() - 0.5) * 1.9;
    const pad = random() > 0.6;

    return {
      x,
      y,
      pad,
      label: pad && labelIndex < NODE_LABELS.length ? NODE_LABELS[labelIndex++] : null
    };
  });

  const edges = [];
  const seen = new Set();

  nodes.forEach((node, index) => {
    const nearest = nodes
      .map((peer, peerIndex) => ({
        peerIndex,
        distance: (node.x - peer.x) ** 2 + (node.y - peer.y) ** 2
      }))
      .filter(({ peerIndex }) => peerIndex !== index)
      .sort((a, b) => a.distance - b.distance);

    const links = node.pad ? 2 : 1;
    for (let link = 0; link < links; link += 1) {
      const { peerIndex } = nearest[link];
      const key = index < peerIndex ? `${index}:${peerIndex}` : `${peerIndex}:${index}`;
      if (!seen.has(key)) {
        seen.add(key);
        edges.push([index, peerIndex]);
      }
    }
  });

  return { nodes, edges };
};

// PCB-style routing: run at 45 degrees until one axis is aligned, then
// finish straight. Returns the polyline plus segment lengths for packets.
const routeTrace = (ax, ay, bx, by) => {
  const deltaX = bx - ax;
  const deltaY = by - ay;
  const diagonal = Math.min(Math.abs(deltaX), Math.abs(deltaY));
  const midX = ax + Math.sign(deltaX) * diagonal;
  const midY = ay + Math.sign(deltaY) * diagonal;
  const firstLength = Math.hypot(midX - ax, midY - ay);
  const secondLength = Math.hypot(bx - midX, by - midY);

  return {
    points: [ax, ay, midX, midY, bx, by],
    firstLength,
    total: firstLength + secondLength || 1
  };
};

const pointOnTrace = (trace, progress) => {
  const [ax, ay, midX, midY, bx, by] = trace.points;
  const distance = clamp(progress, 0, 1) * trace.total;

  if (distance <= trace.firstLength) {
    const t = trace.firstLength ? distance / trace.firstLength : 0;
    return [ax + (midX - ax) * t, ay + (midY - ay) * t];
  }

  const remaining = trace.total - trace.firstLength;
  const t = remaining ? (distance - trace.firstLength) / remaining : 0;
  return [midX + (bx - midX) * t, midY + (by - midY) * t];
};

const initScrollProgress = (progress) => {
  const readout = document.querySelector("[data-scroll-readout]");
  if (!progress && !readout) return;

  let scheduled = false;

  const update = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const ratio = scrollable > 0 ? clamp(window.scrollY / scrollable, 0, 1) : 0;
    if (progress) progress.style.transform = `scaleX(${ratio})`;
    if (readout) readout.textContent = String(Math.round(ratio * 100)).padStart(3, "0");
    scheduled = false;
  };

  const scheduleUpdate = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(update);
  };

  window.addEventListener("scroll", scheduleUpdate, { passive: true });
  window.addEventListener("resize", scheduleUpdate, { passive: true });
  update();
};

export default function initSignalCanvas() {
  const canvas = document.querySelector("[data-signal-canvas]");
  const progress = document.querySelector("[data-scroll-progress]");
  initScrollProgress(progress);

  if (!canvas) return;

  const context = canvas.getContext("2d", { alpha: true });
  if (!context) {
    console.warn("[signal-canvas] A 2D rendering context is unavailable.");
    return;
  }

  const hero = canvas.closest("#hero");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const circuit = createCircuit();
  const pointer = { currentX: 0, currentY: 0, targetX: 0, targetY: 0 };

  let width = 1;
  let height = 1;
  let frame = 0;
  let heroVisible = true;
  let pageVisible = !document.hidden;

  const stop = () => {
    if (!frame) return;
    window.cancelAnimationFrame(frame);
    frame = 0;
  };

  const draw = (time = 0) => {
    context.clearRect(0, 0, width, height);

    pointer.currentX += (pointer.targetX - pointer.currentX) * 0.045;
    pointer.currentY += (pointer.targetY - pointer.currentY) * 0.045;

    const compact = width < 700;
    const centerX = width * (compact ? 0.76 : 0.62) + pointer.currentX * 14;
    const centerY = height * (compact ? 0.44 : 0.5) + pointer.currentY * 10;
    const radius = Math.min(width * (compact ? 0.43 : 0.28), height * 0.42);

    const glow = context.createRadialGradient(
      centerX,
      centerY,
      radius * 0.04,
      centerX,
      centerY,
      radius * 1.25
    );
    glow.addColorStop(0, "rgba(255, 255, 255, 0.09)");
    glow.addColorStop(0.45, "rgba(255, 255, 255, 0.03)");
    glow.addColorStop(1, "rgba(255, 255, 255, 0)");
    context.fillStyle = glow;
    context.fillRect(0, 0, width, height);

    const positions = circuit.nodes.map((node) => [
      centerX + node.x * radius * 0.75,
      centerY + node.y * radius * 0.6
    ]);

    const traces = circuit.edges.map(([from, to]) =>
      routeTrace(positions[from][0], positions[from][1], positions[to][0], positions[to][1])
    );

    context.lineWidth = 1;

    traces.forEach((trace) => {
      const [ax, ay, midX, midY, bx, by] = trace.points;
      context.beginPath();
      context.moveTo(ax, ay);
      context.lineTo(midX, midY);
      context.lineTo(bx, by);
      context.strokeStyle = "rgba(255, 255, 255, 0.11)";
      context.stroke();
    });

    context.font = "500 10px 'IBM Plex Mono', monospace";
    context.textBaseline = "alphabetic";

    circuit.nodes.forEach((node, index) => {
      const [x, y] = positions[index];

      if (node.pad) {
        context.strokeStyle = "rgba(255, 255, 255, 0.4)";
        context.strokeRect(x - 3.5, y - 3.5, 7, 7);
        context.fillStyle = "rgba(255, 255, 255, 0.72)";
        context.fillRect(x - 1, y - 1, 2, 2);
      } else {
        context.fillStyle = "rgba(255, 255, 255, 0.5)";
        context.fillRect(x - 1.5, y - 1.5, 3, 3);
      }

      if (node.label) {
        context.fillStyle = "rgba(255, 255, 255, 0.42)";
        context.fillText(node.label, x + 8, y - 7);
      }
    });

    // CAD-style registration marks around the board
    const marks = [
      [-1.05, -0.85],
      [1.1, -0.62],
      [0.92, 0.98]
    ];
    context.strokeStyle = "rgba(255, 255, 255, 0.22)";
    marks.forEach(([mx, my]) => {
      const x = centerX + mx * radius * 0.75;
      const y = centerY + my * radius * 0.6;
      context.beginPath();
      context.moveTo(x - 6, y);
      context.lineTo(x + 6, y);
      context.moveTo(x, y - 6);
      context.lineTo(x, y + 6);
      context.stroke();
    });

    // Measurement ruler along the base of the board
    const rulerY = centerY + radius * 0.85;
    const rulerStart = centerX - radius * 1.12;
    const rulerEnd = centerX + radius * 1.12;
    context.beginPath();
    context.moveTo(rulerStart, rulerY);
    context.lineTo(rulerEnd, rulerY);
    context.strokeStyle = "rgba(255, 255, 255, 0.14)";
    context.stroke();

    const tickStep = radius * 0.112;
    let tickIndex = 0;
    context.beginPath();
    for (let x = rulerStart; x <= rulerEnd; x += tickStep) {
      const tall = tickIndex % 4 === 0;
      context.moveTo(x, rulerY);
      context.lineTo(x, rulerY - (tall ? 9 : 5));
      tickIndex += 1;
    }
    context.strokeStyle = "rgba(255, 255, 255, 0.2)";
    context.stroke();

    // Packets travelling along the traces
    const motion = reducedMotion.matches ? 0 : time;

    for (let packet = 0; packet < PACKET_COUNT; packet += 1) {
      const speed = 0.00025 + packet * 0.00004;
      const total = motion * speed + packet * 0.61;
      const run = Math.floor(total);
      const progressOnTrace = reducedMotion.matches ? 0.2 + packet * 0.13 : total - run;
      const trace = traces[(run * 13 + packet * 5) % traces.length];
      if (!trace) continue;

      const [headX, headY] = pointOnTrace(trace, progressOnTrace);
      const [tailX, tailY] = pointOnTrace(trace, progressOnTrace - 0.1);

      context.beginPath();
      context.moveTo(tailX, tailY);
      context.lineTo(headX, headY);
      context.strokeStyle = `rgba(${ACCENT}, 0.34)`;
      context.stroke();

      context.save();
      context.shadowBlur = 12;
      context.shadowColor = `rgba(${ACCENT}, 0.7)`;
      context.beginPath();
      context.arc(headX, headY, 1.7, 0, TAU);
      context.fillStyle = `rgba(${ACCENT}, 0.95)`;
      context.fill();
      context.restore();
    }

    if (!reducedMotion.matches && heroVisible && pageVisible) {
      frame = window.requestAnimationFrame(draw);
    } else {
      frame = 0;
    }
  };

  const start = () => {
    if (frame || reducedMotion.matches || !heroVisible || !pageVisible) return;
    frame = window.requestAnimationFrame(draw);
  };

  const resize = () => {
    const bounds = canvas.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, DPR_LIMIT);
    width = Math.max(1, Math.round(bounds.width));
    height = Math.max(1, Math.round(bounds.height));
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    draw(0);
    start();
  };

  const handlePointer = (event) => {
    if (!hero) return;
    const bounds = hero.getBoundingClientRect();
    pointer.targetX = clamp((event.clientX - bounds.left) / bounds.width - 0.5, -0.5, 0.5);
    pointer.targetY = clamp((event.clientY - bounds.top) / bounds.height - 0.5, -0.5, 0.5);
  };

  const resetPointer = () => {
    pointer.targetX = 0;
    pointer.targetY = 0;
  };

  hero?.addEventListener("pointermove", handlePointer, { passive: true });
  hero?.addEventListener("pointerleave", resetPointer, { passive: true });

  document.addEventListener("visibilitychange", () => {
    pageVisible = !document.hidden;
    if (pageVisible) start();
    else stop();
  });

  reducedMotion.addEventListener("change", () => {
    stop();
    draw(0);
    start();
  });

  if ("IntersectionObserver" in window && hero) {
    const observer = new IntersectionObserver(([entry]) => {
      heroVisible = entry.isIntersecting;
      if (heroVisible) start();
      else stop();
    }, { threshold: 0.02 });
    observer.observe(hero);
  }

  if ("ResizeObserver" in window) {
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
  } else {
    window.addEventListener("resize", resize, { passive: true });
  }

  resize();
}
